<template>
  <section
    class="heading-module-component"
    :class="`heading-module-${instance} ${data.orb ? 'has-orb' : ''} ${data.orb_position ? 'orb-left' : 'orb-top'} ${data.horizontal_lines ? 'has-lines' : ''}`">
    <div
      v-if="data.orb"
      class="inner-container top">
      <div
        class="horizontal-line-top"
        :class="topLineH"
        :style="`background-color: ${data.color};`"></div>
      <div
        class="vertical-line-top"
        :class="topLineV"
        :style="`background-color: ${data.color};`"></div>
      <div
        class="orb"
        :class="orb"
        :style="`background-color: ${data.color}; box-shadow: 0 0 20px ${data.color};`"></div>
    </div>
    <div class="inner-container content">
      <div
        class="orb vertical"
        :class="orbVertical"
        :style="`background-color: ${data.color}; box-shadow: 0 0 20px ${data.color};`"></div>
      <div
        class="wrapper"
        :style="`border-top-color: ${data.color}; border-bottom-color: ${data.color_end};`">
        <div
          class="line"
          :style="`background: linear-gradient(180deg, ${data.color} 0%,  ${data.color_end} 100%);`"></div>
        <div
          class="sub-heading-area"
          :class="`${data.width ? 'wide' : ''}`">
          <div
            class="sub-heading"
            :class="`${data.font_type ? 'nuances' : 'telegraf'} ${data.font_size ? 'big' : ''}`"
            v-html="data.heading_area" />
          <div
            class="sub-content"
            :class="`${data.text_width ? 'wide' : ''} ${data.text_color ? 'black' : ''}`"
            v-html="data.text_area" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "HeadingComponent",
  "CLASSNAME": "heading-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */
import gsap from 'gsap'
import useUtils from '@/composables/useUtils'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { onMounted, onBeforeUnmount } from 'vue'

const props = defineProps(['data'])
gsap.registerPlugin(ScrollTrigger)
const { genUUID, device } = useUtils()
const lineId = `line-${genUUID()}`
const topLineH = `top-line-h-${genUUID()}`
const topLineV = `top-line-v-${genUUID()}`
const orb = `orb-${genUUID()}`
const orbVertical = `orb-vertical-${genUUID()}`
const instance = genUUID()

const lineAnimation = () => {
  gsap.fromTo(
    '.heading-module-component .line',
    {
      scaleY: 0,
    },
    {
      scrollTrigger: {
        trigger: '.heading-module-component',
        id: lineId,
        start: 'top center',
        end: 'bottom center',
        markers: false,
        scrub: true,
      },
      scaleY: 1,
    }
  )
}

const newLineAnimation = () => {
  gsap.fromTo(
    `.${topLineV}`,
    { scaleY: 0 },
    {
      scaleY: 1,
      scrollTrigger: {
        trigger: `.heading-module-${instance}`,
        id: topLineV,
        start: 'top+=50 center',
        end: 'top+=200 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${topLineH}`,
    { scaleX: 0 },
    {
      scaleX: 1,
      scrollTrigger: {
        trigger: `.heading-module-${instance}`,
        id: topLineH,
        start: 'top-=100 center',
        end: 'top+=50 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${orb}`,
    { scale: 0, opacity: 0, y: -40 },
    {
      scale: 1,
      opacity: 1,
      y: device.value === 'mobile' ? 20 : 40,
      scrollTrigger: {
        trigger: `.heading-module-${instance}`,
        id: `so-${orb}`,
        start: 'top+=100 center',
        end: 'top+=200 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${orbVertical}`,
    { y: -50 },
    {
      y: 400,
      scrollTrigger: {
        trigger: `.heading-module-${instance}`,
        id: orbVertical,
        start: 'top center',
        end: 'bottom center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${orbVertical}`,
    { scale: 0, opacity: 0 },
    {
      scale: 1,
      opacity: 1,
      scrollTrigger: {
        trigger: `.heading-module-${instance}`,
        id: `so-${orbVertical}`,
        start: 'top center',
        end: 'top+=100 center',
        scrub: true,
        markers: false,
      },
    }
  )

  gsap.fromTo(
    `.${orbVertical}`,
    { scale: 1, opacity: 1 },
    {
      scale: 0,
      opacity: 0,
      scrollTrigger: {
        trigger: `.heading-module-${instance}`,
        id: `so-out-${orbVertical}`,
        start: 'center+=50 center',
        end: 'center+=250 center',
        scrub: true,
        markers: false,
      },
    }
  )
  gsap.set(`.${orbVertical}`, { scale: 0, opacity: 0 })
}

onMounted(() => {
  if (props.data.orb) {
    newLineAnimation()
  } else {
    lineAnimation()
  }
})

onBeforeUnmount(() => {
  ScrollTrigger.getById(lineId)?.kill()
  ScrollTrigger.getById(topLineH)?.kill()
  ScrollTrigger.getById(topLineV)?.kill()
  ScrollTrigger.getById(orb)?.kill()
  ScrollTrigger.getById(orbVertical)?.kill()
  ScrollTrigger.getById(`so-${orbVertical}`)?.kill()
  ScrollTrigger.getById(`so-out-${orbVertical}`)?.kill()
})
</script>

<style lang="scss">
.heading-module-component {
  @apply relative -z-1;
  .inner-container.top {
    .vertical-line-top {
      @apply h-24;
    }
  }
  &.has-orb {
    &.orb-top {
      .inner-container.top {
        @apply relative pb-10 my-0 lg:my-20;
        .horizontal-line-top {
          @apply h-[1px] w-6/12 origin-left;
        }
        .vertical-line-top {
          @apply absolute h-10 lg:h-20 w-[1px] left-1/2 top-0 z-10 origin-top;
        }
        .orb {
          @apply absolute w-6 lg:w-8 h-6 lg:h-8 rounded-full left-[calc(50%-12px)] lg:left-[calc(50%-16px)] blur-[2px] top-2;
        }
      }
      .inner-container {
        .wrapper {
          .sub-heading-area {
            @apply pt-10 lg:pt-4;
          }
        }
      }
    }
    &.orb-left {
      .inner-container.top {
        @apply hidden;
      }
      .orb.vertical {
        @apply absolute w-6 lg:w-8 h-6 lg:h-8 rounded-full left-[calc(35px-16px)] xl:left-[calc(75px-16px)] blur-[2px] z-10 opacity-0;
      }
    }
  }
  &.has-lines {
    .inner-container.content {
      @apply py-10 lg:py-20 pt-0 lg:pt-20;
      .wrapper {
        @apply border-y border-y-lm_lavender;
        .line {
          @apply absolute left-[30px] lg:left-[60px] xl:left-[75px] top-0 h-full w-[1px] bg-lm_lavender origin-top;
        }
      }
    }
  }
  .inner-container {
    .wrapper {
      .sub-heading-area {
        @apply max-w-[1040px] mx-auto py-14;
        &.wide {
          @apply max-w-[1200px];
        }
        .sub-heading {
          @apply text-center mb-10;
          &.nuances {
            * {
              @apply font-nuances;
            }
          }
          &.big {
            * {
              @apply heading-5 lg:heading-2;
            }
          }
          &.telegraf {
            @apply relative w-[105%] md:w-full -left-[3%] md:-left-0;
            * {
              @apply font-telegraf;
            }
          }
          * {
            @apply heading-3 text-lm_purple;
          }
          strong {
            @apply text-lm_orange font-normal;
          }
        }
        .sub-content {
          @apply mt-4;
          @apply text-center max-w-[1080px] mx-auto;
          &.wide {
            @apply max-w-full;
          }
          &.black {
            * {
              @apply text-black;
            }
          }
          * {
            @apply text-lm_purple small lg:large;
          }
          .aligncenter {
            @apply mx-auto mb-6;
          }
        }
      }
    }
  }
}
</style>
